import { Helmet } from "react-helmet-async";

const defaultMeta = {
  siteName: "Chestnut Hill Inn",
  siteUrl: "https://chestnuthillinn.com",
  description: "Experience coastal Maine at Chestnut Hill Inn, a historic circa-1900 seaside inn on Long Island, Maine. Perfect for intimate weddings, family reunions, retreats & island getaways. 7 bedrooms, sleeps 14.",
  image: "https://chestnuthillinn.com/og-image.jpg",
};

export default function SEO({ 
  title, 
  description, 
  canonical,
  image,
  type = "website",
  noindex = false 
}) {
  const fullTitle = title 
    ? `${title} | ${defaultMeta.siteName}` 
    : `${defaultMeta.siteName} | Historic Maine Coastal Inn on Long Island`;
  
  const metaDescription = description || defaultMeta.description;
  const metaImage = image || defaultMeta.image;
  const canonicalUrl = canonical ? `${defaultMeta.siteUrl}${canonical}` : null;

  return (
    <Helmet>
      {/* Basic Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Open Graph */}
      <meta property="og:site_name" content={defaultMeta.siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={metaImage} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
      
      {/* Additional SEO */}
      <meta name="geo.region" content="US-ME" />
      <meta name="geo.placename" content="Long Island, Maine" />
    </Helmet>
  );
}

// Structured Data for Lodging
export function LodgingSchema() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": "Chestnut Hill Inn",
    "description": "Historic circa-1900 coastal inn on Long Island, Maine. Perfect for weddings, family reunions, retreats, and island getaways.",
    "url": "https://chestnuthillinn.com",
    "telephone": "+1-603-858-2272",
    "email": "chestnuthillinn@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "209 Beach Ave",
      "addressLocality": "Long Island",
      "addressRegion": "ME",
      "postalCode": "04050",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "43.6917",
      "longitude": "-70.1628"
    },
    "priceRange": "$800-$900/night",
    "amenityFeature": [
      { "@type": "LocationFeatureSpecification", "name": "Full Kitchen" },
      { "@type": "LocationFeatureSpecification", "name": "WiFi" },
      { "@type": "LocationFeatureSpecification", "name": "Beach Access" },
      { "@type": "LocationFeatureSpecification", "name": "Fire Pit" },
      { "@type": "LocationFeatureSpecification", "name": "Screened Porch" }
    ],
    "numberOfRooms": 7,
    "petsAllowed": true,
    "checkinTime": "16:00",
    "checkoutTime": "10:00",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": "100"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
}
